"use client";
import React, { useState, useEffect } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { IoSearch, IoClose } from "react-icons/io5";
import { CiMoneyBill, CiClock2 } from "react-icons/ci";
import { RiDeleteBin6Line } from "react-icons/ri";
import toast from "react-hot-toast";
import { REST_API_URL } from "../../api/config";

const API_BASE = REST_API_URL;

const getUserTimeZone = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

const buildHeaders = (token, additionalHeaders = {}) => {
  const headers = {
    "Content-Type": "application/json",
    ...additionalHeaders,
  };
  
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  
  const timezone = getUserTimeZone();
  if (timezone) {
    headers["User-Timezone"] = timezone;
  }
  
  return headers;
};

export default function EditInvoiceModal({ onClose, invoice }) {
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [projectName, setProjectName] = useState("");
  const [invoiceDate, setInvoiceDate] = useState("");
  const [paymentDue, setPaymentDue] = useState("");

  const [showCustomerList, setShowCustomerList] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  const [showServiceList, setShowServiceList] = useState(false);
  const [services, setServices] = useState([]);
  const [serviceSearch, setServiceSearch] = useState("");
  const [selectedServices, setSelectedServices] = useState([]);

  const percentages = [0, 5, 10, 15, 20, 25];
  const [showTax, setShowTax] = useState(false);
  const [taxValue, setTaxValue] = useState(5);
  const [showDiscount, setShowDiscount] = useState(false);
  const [discountValue, setDiscountValue] = useState(5);

  const [saving, setSaving] = useState(false);

  const token =
    typeof window !== "undefined"
      ? localStorage.getItem("accessToken") ||
        sessionStorage.getItem("accessToken")
      : null;

  useEffect(() => {
    if (!invoice) return;

    setInvoiceNumber(invoice.invoice_number || "");
    setProjectName(invoice.project_name || "");

    setInvoiceDate(
      invoice.invoice_date ? invoice.invoice_date.slice(0, 10) : ""
    );

    setPaymentDue(
      invoice.payment_due !== undefined && invoice.payment_due !== null
        ? String(invoice.payment_due)
        : ""
    );

    if (invoice.customer_details) {
      setSelectedCustomer({
        id: invoice.customer_details.id,
        name: invoice.customer_details.name,
        email: invoice.customer_details.email,
        image_url: invoice.customer_details.image_url,
      });
    } else {
      setSelectedCustomer(null);
    }

    setSelectedServices(
      Array.isArray(invoice.services_details) ? invoice.services_details : []
    );

    const taxNum =
      typeof invoice.tax === "number"
        ? invoice.tax
        : parseInt(invoice.tax, 10) || 0;
    const discNum =
      typeof invoice.discount === "number"
        ? invoice.discount
        : parseInt(invoice.discount, 10) || 0;

    setTaxValue(taxNum);
    setDiscountValue(discNum);
  }, [invoice]);

  useEffect(() => {
    if (!showCustomerList || !token) return;
    const fetchClients = async () => {
      try {
        const headers = buildHeaders(token);
        
        const res = await axios.get(`${API_BASE}/client/clients-list/`, {
          headers,
        });
        setCustomers(res.data);
      } catch (err) {
        console.error("❌ Failed to load clients:", err);
      }
    };
    fetchClients();
  }, [showCustomerList, token]);

  useEffect(() => {
    if (!showServiceList || !token) return;
    const fetchServices = async () => {
      try {
        const headers = buildHeaders(token);
        
        const res = await axios.get(`${API_BASE}/service/my-services/`, {
          headers,
        });
        setServices(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error("❌ Failed to load services:", err);
      }
    };
    fetchServices();
  }, [showServiceList, token]);

  const filteredCustomers = customers.filter((c) =>
    c.name?.toLowerCase().includes(search.toLowerCase())
  );

  const filteredServices = services.filter((s) =>
    s.name?.toLowerCase().includes(serviceSearch.toLowerCase())
  );

  const handleAddService = (service) => {
    if (!selectedServices.some((s) => s.id === service.id)) {
      setSelectedServices((prev) => [...prev, service]);
    }
    setShowServiceList(false);
  };

  const handleRemoveService = (id) => {
    setSelectedServices((prev) => prev.filter((s) => s.id !== id));
  };

  const handleSave = async () => {
    if (!invoice?.id) {
      toast.error("Invoice ID not found");
      return;
    }
    if (!token) {
      toast.error("Authorization token not found");
      return;
    }

    try {
      setSaving(true);

      const payload = {
        invoice_number: invoiceNumber,
        project_name: projectName,
        invoice_date: invoiceDate
          ? `${invoiceDate}T00:00:00Z`
          : invoice.invoice_date,
        payment_due: paymentDue ? String(paymentDue) : "0",
        customer: selectedCustomer?.id || invoice.customer,
        listings: invoice.listings || [],
        services: selectedServices.map((s) => s.id),
        other_service: invoice.other_service ?? false,
        tax: String(taxValue),
        discount: String(discountValue),
        paid: invoice.paid,
      };

      const headers = buildHeaders(token);
      
      await axios.put(
        `${API_BASE}/invoice/invoice/${invoice.id}/`,
        payload,
        { headers }
      );

      toast.success("Invoice updated successfully");
      onClose?.();
    } catch (err) {
      console.error("❌ Failed to update invoice:", err);
      const msg =
        err?.response?.data?.detail ||
        "Failed to update invoice. Check console.";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 20 }}
        transition={{ duration: 0.3, ease: "easeOut" }}
        className="w-[700px] max-w-full h-auto bg-white border border-[rgba(154,153,171,0.2)] shadow-md rounded-2xl p-8 flex flex-col gap-6 relative"
      >
        <div className="flex justify-between items-center">
          <h2 className="text-[20px] font-bold text-[#302B2B]">
            Edit invoice
          </h2>
          <button
            onClick={onClose}
            className="w-6 h-6 flex items-center justify-center text-gray-700 hover:text-black"
          >
            ✕
          </button>
        </div>

        <div className="flex gap-4">
          <div className="flex flex-col flex-1 gap-2">
            <label className="text-sm text-[#302B2B]">Invoice Number</label>
            <input
              type="text"
              value={invoiceNumber}
              onChange={(e) => setInvoiceNumber(e.target.value)}
              placeholder="Type Invoice number"
              className="border border-[#9A99AB] rounded-[14px] py-3 px-3 text-sm outline-none focus:border-[#5563F6]"
            />
          </div>
          <div className="flex flex-col flex-1 gap-2">
            <label className="text-sm text-[#302B2B]">Project Name</label>
            <input
              type="text"
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              placeholder="Type project name"
              className="border border-[#9A99AB] rounded-[14px] py-3 px-3 text-sm outline-none focus:border-[#5563F6]"
            />
          </div>
        </div>

        <div className="flex gap-4">
          <div className="flex flex-col flex-1 gap-2">
            <label className="text-sm text-[#302B2B]">Invoice Date</label>
            <input
              type="date"
              value={invoiceDate}
              onChange={(e) => setInvoiceDate(e.target.value)}
              className="border border-[#9A99AB] rounded-[14px] py-3 px-3 text-sm outline-none focus:border-[#5563F6]"
            />
          </div>
          <div className="flex flex-col flex-1 gap-2">
            <label className="text-sm text-[#302B2B]">Payment Due (days)</label>
            <input
              type="number"
              min="0"
              value={paymentDue}
              onChange={(e) => setPaymentDue(e.target.value)}
              placeholder="5"
              className="border border-[#9A99AB] rounded-[14px] py-3 px-3 text-sm outline-none focus:border-[#5563F6]"
            />
          </div>
        </div>

        <div className="flex gap-4 relative">
          <div className="flex flex-col flex-1 gap-2">
            <button
              onClick={() => setShowCustomerList((p) => !p)}
              className="border-2 border-[#5563F6] rounded-[14px] py-3 text-[#5563F6] font-medium flex items-center justify-center gap-2 hover:bg-[#F6F8FF] transition-all"
            >
              {selectedCustomer ? "Change customer" : "Add customer"}
              <span className="text-lg leading-none">＋</span>
            </button>

            <AnimatePresence>
              {selectedCustomer && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="flex items-center gap-3 border border-[#EDEEF3] rounded-[14px] px-4 py-3 mt-2"
                >
                  <img
                    src={
                      selectedCustomer.image_url || "/avatar-placeholder.png"
                    }
                    alt={selectedCustomer.name}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                  <span className="flex-1 text-[#302B2B] font-medium">
                    {selectedCustomer.name}
                  </span>
                  <button
                    onClick={() => setSelectedCustomer(null)}
                    className="text-[#9A99AB] hover:text-[#302B2B]"
                  >
                    <IoClose size={18} />
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <div className="flex flex-col flex-1 gap-2">
            <button
              onClick={() => setShowServiceList((p) => !p)}
              className="border-2 border-[#5563F6] rounded-[14px] py-3 text-[#5563F6] font-medium flex items-center justify-center gap-2 hover:bg-[#F6F8FF] transition-all"
            >
              Add item/Service <span className="text-lg leading-none">＋</span>
            </button>

            <AnimatePresence>
              {selectedServices.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="flex flex-col gap-2 mt-2"
                >
                  {selectedServices.map((s) => (
                    <div
                      key={s.id}
                      className="flex justify-between items-center border border-[#EDEEF3] rounded-[14px] px-4 py-5"
                    >
                      <span className="text-[#302B2B] font-medium">
                        {s.name}
                      </span>
                      <div className="flex items-center gap-3">
                        <span className="text-[#302B2B]">
                          {s.price}
                          {s.currency ? ` ${s.currency}` : "$"}
                        </span>
                        <button
                          onClick={() => handleRemoveService(s.id)}
                          className="text-[#9A99AB] hover:text-[#302B2B]"
                        >
                          <RiDeleteBin6Line size={18} />
                        </button>
                      </div>
                    </div>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <AnimatePresence>
            {showCustomerList && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="absolute left-0 top-[60px] w-[331px] bg-white border border-[rgba(154,153,171,0.2)] rounded-2xl shadow-lg p-4 flex flex-col gap-3 z-50"
              >
                <div className="bg-[#F6F6F6] rounded-2xl px-4 py-3 flex items-center gap-2">
                  <IoSearch className="text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="bg-transparent outline-none text-sm flex-1 text-[#302B2B]"
                  />
                </div>
                <div className="max-h-[250px] overflow-y-auto pr-1 custom-scroll">
                  {filteredCustomers.map((c) => (
                    <div
                      key={c.id}
                      className={`flex items-center gap-3 py-2 px-2 rounded-lg cursor-pointer transition ${
                        selectedCustomer?.id === c.id
                          ? "bg-[#F6F8FF] border border-[#5563F6]"
                          : "hover:bg-[#F6F6F6]"
                      }`}
                      onClick={() => {
                        setSelectedCustomer(c);
                        setShowCustomerList(false);
                      }}
                    >
                      <img
                        src={c.image_url || "/avatar-placeholder.png"}
                        alt={c.name}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                      <span className="text-[#302B2B] text-sm">{c.name}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <AnimatePresence>
            {showServiceList && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="absolute right-0 top-[60px] w-[360px] bg-white border border-[rgba(154,153,171,0.2)] rounded-2xl shadow-lg p-4 flex flex-col gap-3 z-50"
              >
                <div className="bg-[#F6F6F6] rounded-2xl px-4 py-3 flex items-center gap-2">
                  <IoSearch className="text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search"
                    value={serviceSearch}
                    onChange={(e) => setServiceSearch(e.target.value)}
                    className="bg-transparent outline-none text-sm flex-1 text-[#302B2B]"
                  />
                </div>
                <div className="max-h-[250px] overflow-y-auto">
                  {filteredServices.map((s) => (
                    <div
                      key={s.id}
                      onClick={() => handleAddService(s)}
                      className="border border-[#EDEEF3] rounded-xl p-3 mb-2 hover:bg-[#F6F6F6] cursor-pointer"
                    >
                      <h4 className="font-semibold text-[#302B2B]">{s.name}</h4>
                      <p className="text-[#9A99AB] text-sm">
                        {s.description || "No description"}
                      </p>
                      <div className="flex items-center gap-4 text-sm text-[#302B2B] mt-2">
                        <span className="flex items-center gap-1">
                          <CiMoneyBill /> Price: {s.price || "—"}{" "}
                          {s.currency || "USD"}
                        </span>
                        <span className="flex items-center gap-1">
                          <CiClock2 /> Duration: {s.duration || "—"} min
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <div className="flex gap-4">
          <div className="flex flex-col flex-1 gap-2 relative">
            <label className="text-sm text-[#302B2B]">Tax</label>
            <div
              onClick={() => setShowTax((prev) => !prev)}
              className="flex items-center justify-between border border-[#9A99AB] rounded-[14px] py-3 px-3 text-sm text-[#302B2B] cursor-pointer bg-white hover:border-[#5563F6] transition"
            >
              <span>{`TAX ${taxValue}%`}</span>
            </div>

            <AnimatePresence>
              {showTax && (
                <motion.div
                  initial={{ opacity: 0, y: -5 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -5 }}
                  transition={{ duration: 0.2 }}
                  className="absolute z-50 top-[70px] left-0 w-full bg-white border border-[#EDEEF3] rounded-2xl shadow-lg p-2"
                >
                  {percentages.map((p) => (
                    <div
                      key={p}
                      onClick={() => {
                        setTaxValue(p);
                        setShowTax(false);
                      }}
                      className="flex justify-between items-center px-4 py-2 rounded-lg cursor-pointer hover:bg-[#F6F6F6]"
                    >
                      <span className="text-[#302B2B] text-sm">{p}%</span>
                    </div>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <div className="flex flex-col flex-1 gap-2 relative">
            <label className="text-sm text-[#302B2B]">Discount</label>
            <div
              onClick={() => setShowDiscount((prev) => !prev)}
              className="flex items-center justify-between border border-[#9A99AB] rounded-[14px] py-3 px-3 text-sm text-[#302B2B] cursor-pointer bg-white hover:border-[#5563F6] transition"
            >
              <span>{`Discount ${discountValue}%`}</span>
            </div>

            <AnimatePresence>
              {showDiscount && (
                <motion.div
                  initial={{ opacity: 0, y: -5 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -5 }}
                  transition={{ duration: 0.2 }}
                  className="absolute z-50 top-[70px] left-0 w-full bg-white border border-[#EDEEF3] rounded-2xl shadow-lg p-2"
                >
                  {percentages.map((p) => (
                    <div
                      key={p}
                      onClick={() => {
                        setDiscountValue(p);
                        setShowDiscount(false);
                      }}
                      className="flex justify-between items-center px-4 py-2 rounded-lg cursor-pointer hover:bg-[#F6F6F6]"
                    >
                      <span className="text-[#302B2B] text-sm">{p}%</span>
                    </div>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        <div className="flex justify-end gap-4 mt-auto">
          <button
            onClick={onClose}
            className="text-[#9A99AB] text-[16px] hover:text-[#302B2B]"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="bg-[#5563F6] text-white rounded-[14px] px-6 py-3 hover:bg-[#3f4de0] disabled:opacity-60"
          >
            {saving ? "Saving..." : "Save changes"}
          </button>
        </div>
      </motion.div>
    </div>
  );
}